import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Api, ApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button, TextField, Card } from "../../components/ui";
import "./auth.css";

// hCaptcha site key is public by design (it's embedded in client-side JS on
// every site that uses hCaptcha), so it's fine to read from an env var baked
// in at build time rather than treating it as a secret.
const HCAPTCHA_SITE_KEY = import.meta.env.VITE_HCAPTCHA_SITE_KEY;

// No server-settings gear icon here, unlike the desktop app's version --
// that exists there so self-hosting power users can point the app at
// their own backend. This is a public website with one fixed backend
// (lib/config.js), so there's nothing for a visitor to configure.
export default function Login() {
  const [mode, setMode] = useState("password"); // password | otp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Set once the backend tells us this account has too many recent failed
  // attempts. Shown as a banner and forces the OTP tab so the user isn't
  // stuck retrying a password path that will keep getting rejected.
  const [otpRequired, setOtpRequired] = useState(false);

  // Set once the backend tells us this account needs a CAPTCHA before the
  // next password attempt. Cleared on a successful login.
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaWidgetRef = useRef(null);
  const captchaWidgetId = useRef(null);

  const { login } = useAuth();
  const navigate = useNavigate();

  function goNext(next) {
    navigate(next === "plan" ? "/plan" : "/app/monitor", { replace: true });
  }

  // Load the hCaptcha script lazily -- only once it's actually needed --
  // rather than on every visit to the login page.
  useEffect(() => {
    if (!captchaRequired || !HCAPTCHA_SITE_KEY) return;
    if (window.hcaptcha) {
      renderCaptcha();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://js.hcaptcha.com/1/api.js?render=explicit";
    script.async = true;
    script.onload = renderCaptcha;
    document.body.appendChild(script);
    // Intentionally not removing the script on cleanup: hCaptcha's own
    // script guards against double-injection, and removing it mid-session
    // can orphan an already-rendered widget.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaRequired]);

  function renderCaptcha() {
    if (!window.hcaptcha || !captchaWidgetRef.current || captchaWidgetId.current !== null) return;
    captchaWidgetId.current = window.hcaptcha.render(captchaWidgetRef.current, {
      sitekey: HCAPTCHA_SITE_KEY,
      callback: (token) => setCaptchaToken(token),
      "expired-callback": () => setCaptchaToken(null),
    });
  }

  function resetCaptcha() {
    setCaptchaToken(null);
    if (window.hcaptcha && captchaWidgetId.current !== null) {
      window.hcaptcha.reset(captchaWidgetId.current);
    }
  }

  async function submitPassword(e) {
    e.preventDefault();

    if (captchaRequired && !captchaToken) {
      setError("Please complete the CAPTCHA to continue.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const resp = await Api.login(email, password, captchaRequired ? captchaToken : undefined);
      await login(resp.token);
      goNext(resp.next);
    } catch (err) {
      // Backend signals these two cases via response body fields rather than
      // status text alone, so ApiError needs to expose the parsed payload.
      const payload = err instanceof ApiError ? err.payload : null;

      if (payload?.otp_required) {
        setOtpRequired(true);
        setMode("otp");
        setError(null);
      } else if (payload?.captcha_required) {
        setCaptchaRequired(true);
        resetCaptcha();
        setError("Too many failed attempts -- please complete the CAPTCHA and try again.");
      } else {
        setError(err instanceof ApiError ? err.message : "Invalid email or password");
        resetCaptcha();
      }
    } finally {
      setLoading(false);
    }
  }

  async function sendCode() {
    setLoading(true);
    setError(null);
    try {
      await Api.requestOtp(email);
      setOtpSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Network error");
    } finally {
      setLoading(false);
    }
  }

  async function verifyCode() {
    setLoading(true);
    setError(null);
    try {
      const resp = await Api.verifyOtp(email, otpCode);
      await login(resp.token);
      goNext(resp.next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Invalid or expired code");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="tg-auth-screen">
      <Card className="tg-auth-card">
        <h1 style={{ fontSize: 22 }}>Welcome back</h1>
        <p style={{ color: "var(--slate)", fontSize: 13.5, marginTop: 6 }}>
          Sign in to keep an eye on your token usage.
        </p>

        {otpRequired && (
          <div className="tg-auth-banner" role="alert" style={{ marginTop: 12 }}>
            For your account's security, we've temporarily disabled password
            login after several failed attempts. Please sign in with a
            one-time code sent to your email instead.
          </div>
        )}

        <div className="tg-tabs">
          <button
            className={`tg-tabs__tab ${mode === "password" ? "tg-tabs__tab--active" : ""}`}
            disabled={otpRequired}
            title={otpRequired ? "Password login is temporarily disabled for this account" : undefined}
            onClick={() => {
              if (otpRequired) return;
              setMode("password");
              setError(null);
            }}
          >
            Email &amp; Password
          </button>
          <button
            className={`tg-tabs__tab ${mode === "otp" ? "tg-tabs__tab--active" : ""}`}
            onClick={() => {
              setMode("otp");
              setError(null);
            }}
          >
            Email OTP
          </button>
        </div>

        {mode === "password" ? (
          <form onSubmit={submitPassword} className="tg-auth-form">
            <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div style={{ textAlign: "right", marginTop: -6 }}>
              <Link to="/forgot-password" className="tg-link" style={{ fontSize: 12.5 }}>
                Forgot password?
              </Link>
            </div>

            {captchaRequired && HCAPTCHA_SITE_KEY && (
              <div ref={captchaWidgetRef} style={{ margin: "4px 0" }} />
            )}

            {error && <p className="tg-auth-error">{error}</p>}
            <Button type="submit" fullWidth disabled={loading || (captchaRequired && !captchaToken)}>
              {loading ? "Logging in…" : "Log in"}
            </Button>
          </form>
        ) : (
          <div className="tg-auth-form">
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {!otpSent ? (
              <Button onClick={sendCode} fullWidth disabled={loading || !email}>
                {loading ? "Sending…" : "Send code"}
              </Button>
            ) : (
              <>
                <TextField label="6-digit code" value={otpCode} onChange={(e) => setOtpCode(e.target.value)} />
                <Button onClick={verifyCode} fullWidth disabled={loading || !otpCode}>
                  {loading ? "Verifying…" : "Verify code"}
                </Button>
              </>
            )}
            {error && <p className="tg-auth-error">{error}</p>}
          </div>
        )}

        <div style={{ marginTop: 18, textAlign: "center" }}>
          <Link to="/register" className="tg-link">
            Don&apos;t have an account? Sign up
          </Link>
        </div>
      </Card>
    </div>
  );
}
